/**
 * The three advanced Shopify permissions Mora can ask a store for, and the pure
 * logic that turns a LIVE scope read into a state per card.
 *
 * 🔴 THE GRANT MECHANISM IS THE DOCUMENTED ONE, NOT THE CAPTURED ONE.
 * `docs/research/competitors/marketer-com/OBSERVED-2026-08-27-shopify-advanced-permissions.md`
 * records a competitor's grant links in the shape
 * `admin.shopify.com/store/{handle}/app/grant?access_change_uuid=…&client_id=…`.
 * That shape appears NOWHERE in Shopify's documentation — the access-scopes
 * guide, the App Bridge Scopes API reference and the optional-scopes changelog
 * were all searched with `@shopify/dev-mcp` on 2026-08-27 and returned zero hits
 * for `/app/grant` or `access_change_uuid`. No documented API hands an app an
 * `access_change_uuid`, so Mora could not construct that URL even if it wanted
 * to. The documented request URL, fetched verbatim from
 * https://shopify.dev/docs/apps/build/authentication-authorization/manage-access-scopes
 * ("Browser redirect URL"), is:
 *
 *   https://admin.shopify.com/store/{STORE_NAME}/oauth/install?client_id={CLIENT_ID}&optional_scopes={REQUESTED_SCOPES}
 *
 * and `REQUESTED_SCOPES` "must be a subset of the declared optional_scopes in
 * your TOML file". That subset rule is why {@link resolveShopifyPermission}
 * refuses to build a URL for a scope the live app has not declared: the link
 * would open a Shopify page that cannot grant anything, which is a button that
 * lies.
 *
 * Mora is `embedded = false` (`shopify.app.toml:5`), so the App Bridge path
 * (`shopify.scopes.request()`, the in-app modal the competitor's flow rides on)
 * is not available to this app at all. The redirect URL is the only path.
 *
 * 🔴 A SCOPE IN THE REQUIRED `scopes` FIELD CANNOT BE REQUESTED DYNAMICALLY.
 * Same doc: "Access scopes configured in the scopes field can't be requested
 * dynamically." Its remedy is a reconnect, which is the state
 * {@link ShopifyPermissionState} calls `reconnect` — and it is not theoretical:
 * on 2026-08-27 both live Shopify connections stored
 * `read_customers,read_orders,write_products` while `shopify.app.toml` declares
 * five required scopes.
 */
import { SHOPIFY_DECLARED_REQUIRED_SCOPES } from './shopify-declared-scopes';

/** Scope handles, lowercased — Shopify returns them lowercase on `accessScopes`. */
export interface ShopifyScopeSnapshot {
  /** Granted on THIS store, read live from `currentAppInstallation.accessScopes`. */
  granted: string[];
  /** Declared in the app's required `scopes` field (`App.requestedAccessScopes`). */
  declaredRequired: string[];
  /** Declared in the app's `optional_scopes` (`App.optionalAccessScopes`). */
  declaredOptional: string[];
}

export type ShopifyPermissionId =
  'full-order-history' | 'customer-analytics' | 'push-product-images';

export interface ShopifyPermissionSpec {
  id: ShopifyPermissionId;
  name: string;
  /** One line: what the permission is FOR. */
  purpose: string;
  /** One plain sentence naming what is blocked without it. Never blames Shopify. */
  blocked: string;
  /** The real Shopify scope handles. Verified against shopify.dev, not guessed. */
  scopes: string[];
  /**
   * Scopes Shopify documents this one as being used "in conjunction with".
   * Named for the reader; never requested on their own by this surface.
   */
  companionScopes?: string[];
  /**
   * Set when Shopify gates the DATA behind an app-level review as well as the
   * scope, so a granted scope alone does not prove the figures will fill in.
   * Mora cannot read that review's outcome from a store, so the card says so
   * instead of implying a merchant click resolves it.
   */
  reviewNote?: string;
  /** Shown when Shopify granted the scope but Mora has not shipped the feature. */
  enabledNote?: string;
  /** Why the permission is not offered yet, when Mora has not declared it. */
  undeclaredNote?: string;
}

/**
 * Every scope string below was read off shopify.dev with `@shopify/dev-mcp` on
 * 2026-08-27, not recalled:
 *  · `read_all_orders` — "All relevant orders rather than the default window of
 *    orders created within the last 60 days", marked *permissions required*, and
 *    "used in conjunction with existing order scopes, for example read_orders".
 *    https://shopify.dev/docs/api/usage/access-scopes
 *  · `read_customers` — the scope behind `Order.customer`, whose `numberOfOrders`
 *    and `amountSpent` are the new-vs-returning and lifetime-value fields.
 *  · `write_products` — the scope `push-image-to-shopify` already exercises.
 */
export const SHOPIFY_PERMISSIONS: readonly ShopifyPermissionSpec[] = [
  {
    id: 'full-order-history',
    name: 'Full order history',
    purpose: 'Read every order this store has ever taken, not just the recent window.',
    blocked:
      'Shopify returns only orders from the last 60 days and reports nothing about the rest, so revenue, order counts and average order value before then read as zero rather than as missing.',
    scopes: ['read_all_orders'],
    companionScopes: ['read_orders'],
    enabledNote:
      'Shopify granted access to orders older than 60 days. Mora’s current sync is still a trailing window; the Data coverage section below shows what has actually landed.',
    undeclaredNote:
      'Shopify reviews every app that asks to read orders older than 60 days, and Mora has not been granted that yet. The 60-day window is Shopify’s default for every app, so nothing is wrong with your store.',
  },
  {
    id: 'customer-analytics',
    name: 'Customer analytics',
    purpose: 'Tell a first-time buyer from a returning one, and follow repeat purchases over time.',
    blocked:
      'Orders arrive without the customer identity that links repeat purchases together, so new versus returning, repeat purchase rate and customer lifetime value stay unmeasured.',
    scopes: ['read_customers'],
    reviewNote:
      'Shopify gates customer identity behind an app-level data review as well as this scope. Mora cannot read that review’s outcome from your store, so a granted scope here does not on its own mean the customer figures fill in.',
  },
  {
    id: 'push-product-images',
    name: 'Push enhanced images',
    purpose: "Send an edited product image back to that product's gallery in Shopify.",
    blocked:
      'Edited images stay in Mora, and adding one to a product means uploading it in Shopify by hand.',
    scopes: ['write_products'],
  },
];

/**
 * The concrete cost of missing a REQUIRED scope — one sentence, no invented
 * specifics. Each entry is traced to the exercised code path that names it
 * (`shopify.app.toml`'s own per-scope comment, or a `SHOPIFY_PERMISSIONS`
 * `blocked` sentence already verified for that scope), so this can never grow
 * a claim nobody checked.
 *
 * 🔴 `read_reports` intentionally does NOT name the six-row "Total sales
 * breakdown" (gross/discounts/returns/net/shipping/taxes). That breakdown is
 * reachable two ways — verified against `docs/planning/mora-growth-
 * intelligence-overhaul-2026-08-26/44-COMMERCE-METRIC-DERIVATIONS.md` and
 * independently re-checked with `node scripts/shopify-dev-mcp.mjs` on
 * 2026-08-27: Tier A (`Order` line-item/discount/shipping/tax money fields)
 * needs only `read_orders` — a scope both live connections already hold — and
 * is blocked on an INGEST change, not a scope; Tier B (ShopifyQL `FROM sales
 * SHOW gross_sales, discounts, returns, net_sales, shipping_charges, taxes`)
 * does need `read_reports`, but ALSO needs Shopify's Protected Customer Data
 * Level 2 review before `shopifyqlQuery` will run at all. Mora's deployed
 * fetcher calls it and degrades only the funnel fields when Shopify withholds
 * the response. Naming the six-row
 * breakdown as this scope's consequence would overstate what one reconnect
 * fixes. What `read_reports` actually, exclusively gates today — proven live
 * 2026-07-25 and reachable no other way — is the storefront funnel.
 */
const REQUIRED_SCOPE_CONSEQUENCES: Record<string, string> = {
  read_products:
    'The product picker, the catalog sync, and Shopify’s own product-update webhooks cannot see this store’s products.',
  read_orders:
    'Revenue, order counts, average order value, and the order-line webhooks that keep them current stop reading this store.',
  read_reports:
    'Mora cannot query this store’s storefront funnel — sessions, cart additions, reached checkout, completed checkout — or its conversion rate. ShopifyQL is the only Admin API path to a session object, and it requires this permission.',
  read_inventory: 'Without Product costs, Mora cannot show margin on a campaign.',
};

/**
 * Falls back to a matching `SHOPIFY_PERMISSIONS` card's own `blocked` sentence
 * when one exists for the scope (keeps `write_products`/`read_customers`
 * worded identically in both surfaces), then to a scope-named sentence that
 * invents no specifics — never silently prints nothing for a scope this
 * module has not been taught about yet.
 */
export function shopifyRequiredScopeConsequence(scope: string): string {
  const key = scope.trim().toLowerCase();
  if (REQUIRED_SCOPE_CONSEQUENCES[key]) return REQUIRED_SCOPE_CONSEQUENCES[key];
  const matchingSpec = SHOPIFY_PERMISSIONS.find((spec) =>
    spec.scopes.some((specScope) => specScope.toLowerCase() === key),
  );
  if (matchingSpec) return matchingSpec.blocked;
  return `Mora cannot use the ${shopifyScopeDisplayName(scope)} permission on this store.`;
}

export type ShopifyPermissionState =
  /** The live read did not answer. Never rendered as "off". */
  | 'unknown'
  /** Every scope this permission needs is granted on this store. */
  | 'enabled'
  /** Missing, and every missing scope is declared optional — a request URL exists. */
  | 'grantable'
  /** Missing, but declared REQUIRED, which Shopify forbids requesting dynamically. */
  | 'reconnect'
  /** Missing, and Mora has not declared it to Shopify at all. No URL can exist. */
  | 'undeclared';

export interface ShopifyPermissionStatus {
  spec: ShopifyPermissionSpec;
  state: ShopifyPermissionState;
  /** The scope handles this store is missing. Empty when enabled or unknown. */
  missing: string[];
  /**
   * True when this store granted the scope at install and no longer holds it —
   * the only evidence that justifies the word "Restore" over "Grant".
   */
  previouslyGranted: boolean;
  /** The documented optional-scope request URL, or null when none can be built. */
  grantUrl: string | null;
}

function normalize(scopes: readonly string[] | null | undefined): string[] {
  if (!Array.isArray(scopes)) return [];
  return scopes
    .filter((scope): scope is string => typeof scope === 'string')
    .map((scope) => scope.trim().toLowerCase())
    .filter(Boolean);
}

/** `javacityroasters.myshopify.com` → `javacityroasters`. */
export function shopifyStoreHandle(shopDomain: string | null | undefined): string | null {
  const trimmed = (shopDomain ?? '').trim().toLowerCase();
  if (!trimmed) return null;
  const handle = trimmed.replace(/^https?:\/\//, '').replace(/\.myshopify\.com.*$/, '');
  return handle.split('/')[0] || null;
}

/**
 * The documented "Browser redirect URL" for requesting optional scopes on a
 * standalone app. Returns null rather than a half-built link when either the
 * store handle or the app's client id is missing — a grant button that opens a
 * broken Shopify page is worse than no button.
 */
export function buildShopifyOptionalScopeUrl(
  shopDomain: string | null | undefined,
  clientId: string | null | undefined,
  scopes: readonly string[],
): string | null {
  const handle = shopifyStoreHandle(shopDomain);
  const id = (clientId ?? '').trim();
  const requested = normalize(scopes);
  if (!handle || !id || requested.length === 0) return null;
  const params = new URLSearchParams({ client_id: id, optional_scopes: requested.join(',') });
  return `https://admin.shopify.com/store/${encodeURIComponent(handle)}/oauth/install?${params.toString()}`;
}

export interface ResolveShopifyPermissionArgs {
  spec: ShopifyPermissionSpec;
  /** Null when the live read has not answered — the whole card goes `unknown`. */
  live: ShopifyScopeSnapshot | null;
  /** Scopes recorded at install time. Evidence for `Restore`, never for status. */
  installTimeScopes?: readonly string[] | null;
  shopDomain?: string | null;
  clientId?: string | null;
}

export function resolveShopifyPermission({
  spec,
  live,
  installTimeScopes,
  shopDomain,
  clientId,
}: ResolveShopifyPermissionArgs): ShopifyPermissionStatus {
  if (!live) {
    return { spec, state: 'unknown', missing: [], previouslyGranted: false, grantUrl: null };
  }

  const granted = new Set(normalize(live.granted));
  const required = new Set(normalize(live.declaredRequired));
  const optional = new Set(normalize(live.declaredOptional));
  const needed = normalize(spec.scopes);
  const missing = needed.filter((scope) => !granted.has(scope));

  if (missing.length === 0) {
    return { spec, state: 'enabled', missing: [], previouslyGranted: false, grantUrl: null };
  }

  const installed = new Set(normalize(installTimeScopes));
  const previouslyGranted = missing.some((scope) => installed.has(scope));

  // Required scopes cannot be requested dynamically — Shopify's own words. The
  // remedy is a reconnect, which re-runs managed install against the current
  // declaration.
  if (missing.some((scope) => required.has(scope))) {
    return { spec, state: 'reconnect', missing, previouslyGranted, grantUrl: null };
  }

  if (missing.every((scope) => optional.has(scope))) {
    return {
      spec,
      state: 'grantable',
      missing,
      previouslyGranted,
      grantUrl: buildShopifyOptionalScopeUrl(shopDomain, clientId, missing),
    };
  }

  return { spec, state: 'undeclared', missing, previouslyGranted, grantUrl: null };
}

/**
 * Where "declared required" and "granted" came from. `live` is Shopify's own
 * `currentAppInstallation`/`App.requestedAccessScopes` read, fresher but
 * CORS-locked to the production host (`shopify-admin-proxy`) — unreachable
 * from localhost. `install-time-snapshot` falls back to
 * `SHOPIFY_DECLARED_REQUIRED_SCOPES` (parsed from `shopify.app.toml` itself,
 * so it moves with the file — see `shopify-declared-scopes.ts`) compared
 * against the scopes Shopify returned when this connection last completed
 * OAuth (`connection_data.scopes`, written verbatim from the token response in
 * `shopify-oauth-callback/index.ts:300`). That snapshot is stale evidence for
 * a scope this store could have gained or lost since — see
 * `readInstallTimeScopes`'s own docblock — but a REQUIRED scope can only
 * change via a full reconnect, which rewrites this same column, so as a
 * REQUIRED-scope comparison specifically it stays accurate until the next
 * toml change, not just until the next live read.
 */
export type ShopifyRequiredScopeDriftSource = 'live' | 'install-time-snapshot';

export interface ShopifyRequiredScopeDrift {
  /** Every scope `shopify.app.toml` currently declares required, in declared order. */
  declaredRequired: string[];
  /** Declared-but-not-granted, in declared order. Empty means fully caught up. */
  missing: string[];
  grantedCount: number;
  totalCount: number;
  source: ShopifyRequiredScopeDriftSource;
}

/**
 * THE GENERAL CHECK. Compares whatever the caller says Mora currently declares
 * required against whatever the caller says this store has granted — no scope
 * name or count from today is baked in here, so this keeps working the moment
 * `shopify.app.toml` gains or drops a required scope.
 */
export function detectShopifyRequiredScopeDrift(args: {
  declaredRequired: readonly string[];
  grantedScopes: readonly string[];
  source: ShopifyRequiredScopeDriftSource;
}): ShopifyRequiredScopeDrift {
  const declaredRequired = normalize(args.declaredRequired);
  const granted = new Set(normalize(args.grantedScopes));
  const missing = declaredRequired.filter((scope) => !granted.has(scope));
  return {
    declaredRequired,
    missing,
    grantedCount: declaredRequired.length - missing.length,
    totalCount: declaredRequired.length,
    source: args.source,
  };
}

export interface ShopifyPermissionsSummary {
  statuses: ShopifyPermissionStatus[];
  /** Null when the live read has not answered — the header must not print "0". */
  enabledCount: number | null;
  total: number;
  /** The first permission that is not enabled, for the header trailer. */
  firstGapName: string | null;
  /** Missing scopes the app declares as REQUIRED. Drives the reconnect banner. */
  missingRequiredScopes: string[];
  /** The full drift finding behind `missingRequiredScopes`, incl. its source and the granted/total count. */
  requiredScopeDrift: ShopifyRequiredScopeDrift;
  /** Every optional scope this store could be asked for in one approval screen. */
  grantAllScopes: string[];
  grantAllUrl: string | null;
}

export function summarizeShopifyPermissions(args: {
  live: ShopifyScopeSnapshot | null;
  installTimeScopes?: readonly string[] | null;
  shopDomain?: string | null;
  clientId?: string | null;
  specs?: readonly ShopifyPermissionSpec[];
}): ShopifyPermissionsSummary {
  const specs = args.specs ?? SHOPIFY_PERMISSIONS;
  const statuses = specs.map((spec) =>
    resolveShopifyPermission({
      spec,
      live: args.live,
      installTimeScopes: args.installTimeScopes,
      shopDomain: args.shopDomain,
      clientId: args.clientId,
    }),
  );

  // Prefer a successful live read (fresher, and covers a scope Shopify
  // declared after this build shipped); fall back to the toml's own declared
  // list plus the stored install-time snapshot the moment the live read is
  // unavailable — CORS-locked from localhost, or simply not yet answered.
  const requiredScopeDrift = args.live
    ? detectShopifyRequiredScopeDrift({
        declaredRequired: args.live.declaredRequired,
        grantedScopes: args.live.granted,
        source: 'live',
      })
    : detectShopifyRequiredScopeDrift({
        declaredRequired: SHOPIFY_DECLARED_REQUIRED_SCOPES,
        grantedScopes: args.installTimeScopes ?? [],
        source: 'install-time-snapshot',
      });

  const grantAllScopes = Array.from(
    new Set(
      statuses.filter((status) => status.state === 'grantable').flatMap((status) => status.missing),
    ),
  );

  return {
    statuses,
    enabledCount: args.live ? statuses.filter((s) => s.state === 'enabled').length : null,
    total: statuses.length,
    // An undeclared permission is not a merchant gap yet: there is no action
    // available until the Shopify app configuration is deployed. Keep the
    // header from saying "missing" while the card honestly says "Not offered
    // yet"; once declared, grantable/reconnect states surface here naturally.
    firstGapName:
      statuses.find((s) => s.state === 'grantable' || s.state === 'reconnect')?.spec.name ?? null,
    missingRequiredScopes: requiredScopeDrift.missing,
    requiredScopeDrift,
    grantAllScopes,
    grantAllUrl:
      grantAllScopes.length > 1
        ? buildShopifyOptionalScopeUrl(args.shopDomain, args.clientId, grantAllScopes)
        : null,
  };
}

/**
 * A merchant-readable name for a raw scope handle. Only the scopes Mora actually
 * declares are listed; anything else falls back to the handle itself rather than
 * to an invented description.
 */
const SCOPE_DISPLAY_NAMES: Record<string, string> = {
  read_products: 'Product catalog',
  write_products: 'Product updates',
  read_orders: 'Orders',
  read_all_orders: 'Full order history',
  read_customers: 'Customer analytics',
  read_reports: 'Storefront reporting',
  read_inventory: 'Product costs',
  read_discounts: 'Discount codes',
  write_discounts: 'Discount codes',
};

/**
 * `a`, `a and b`, `a, b and c`. A comma-spliced list ("asked for Product
 * catalog, Storefront reporting") reads as a truncated sentence in the one
 * place a merchant is deciding whether to trust the message.
 */
export function formatScopeList(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function shopifyScopeDisplayName(scope: string): string {
  return SCOPE_DISPLAY_NAMES[scope.trim().toLowerCase()] ?? scope;
}

export function readShopifyInstallTimeScopes(account: { rawData: unknown }): string[] {
  const raw = account.rawData as { connection_data?: { scopes?: unknown } | null } | null;
  const stored = raw?.connection_data?.scopes;
  if (typeof stored !== 'string') return [];
  return stored.split(',');
}

export function shopifyMissingRequiredScopes(account: { rawData: unknown }): string[] {
  const installTime = readShopifyInstallTimeScopes(account);
  if (installTime.length === 0) return [];
  return detectShopifyRequiredScopeDrift({
    declaredRequired: SHOPIFY_DECLARED_REQUIRED_SCOPES,
    grantedScopes: installTime,
    source: 'install-time-snapshot',
  }).missing;
}
